import React from "react";
import {
  Box,
  Container,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Helmet } from "react-helmet-async";

type Faq = {
  id: string;
  question: string;
  answer: string;
};

type Props = {
  faqs: Faq[];
  title?: string;
};

export default function FaqAccordion({
  faqs,
  title = "Frequently Asked Questions",
}: Props) {
  const [expanded, setExpanded] = React.useState<string | false>(false);

  if (!faqs.length) return null;

  // Auto-generate FAQ schema for SEO
  // Google uses this to show expandable FAQs directly in search results
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((faq) => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.answer,
      },
    })),
  };

  return (
    <>
      {/* FAQ Schema injected into head for Google rich results */}
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      <Box
        sx={{
          backgroundColor: "#fff",
          py: { xs: 6, md: 10 },
          borderTop: "1px solid rgba(0,0,0,0.06)",
        }}
      >
        <Container maxWidth="md">
          <Typography
            component="h2"
            sx={{
              fontFamily: `"Bodoni Moda", serif`,
              fontWeight: 600,
              fontSize: { xs: "1.8rem", md: "2.4rem" },
              textAlign: "center",
              mb: { xs: 4, md: 6 },
              letterSpacing: 0.3,
              color: "#111",
            }}
          >
            {title}
          </Typography>

          <Box>
            {faqs.map((faq, index) => (
              <Accordion
                key={faq.id}
                expanded={expanded === faq.id}
                onChange={(_, isExpanded) =>
                  setExpanded(isExpanded ? faq.id : false)
                }
                disableGutters
                elevation={0}
                sx={{
                  borderBottom: "1px solid rgba(0,0,0,0.08)",
                  "&:first-of-type": {
                    borderTop: "1px solid rgba(0,0,0,0.08)",
                  },
                  "&:before": { display: "none" },
                  backgroundColor: "transparent",
                }}
              >
                <AccordionSummary
                  expandIcon={
                    <ExpandMoreIcon
                      sx={{
                        color: "#111",
                        transition: "transform 0.3s ease",
                      }}
                    />
                  }
                  sx={{
                    px: 0,
                    py: 1,
                    minHeight: 64,
                    "& .MuiAccordionSummary-content": {
                      my: 1.5,
                    },
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: `"Bodoni Moda", serif`,
                      fontWeight: 600,
                      fontSize: { xs: "1rem", md: "1.1rem" },
                      color: "#111",
                      pr: 2,
                    }}
                  >
                    {faq.question}
                  </Typography>
                </AccordionSummary>

                <AccordionDetails sx={{ px: 0, pb: 2.5 }}>
                  <Typography
                    sx={{
                      fontSize: "0.95rem",
                      lineHeight: 1.8,
                      color: "rgba(0,0,0,0.7)",
                    }}
                  >
                    {faq.answer}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        </Container>
      </Box>
    </>
  );
}
